import { API_BASE_URL } from "../config";

export const MAX_CONTENT_IMAGES = 20;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Collect display URLs from a post or event.
 * @param {{ images?: Array<string|{url?: string}> } | null | undefined} item
 * @returns {string[]}
 */
export function getContentImageUrls(item) {
  if (!item || !Array.isArray(item.images) || !item.images.length) return [];
  return item.images
    .map((image) => (typeof image === "string" ? image : image?.url || ""))
    .map((url) => String(url).trim())
    .filter(Boolean);
}

/**
 * Validate a local image file before upload.
 * @param {File} file
 * @returns {string} Error message, or empty string if valid
 */
export function validateImageFile(file) {
  if (!file?.type?.startsWith("image/")) {
    return "Please select an image file";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "Image size must be less than 10MB";
  }
  return "";
}

/**
 * Upload a file via the existing server-side S3 endpoint.
 * @param {File} selectedFile
 * @param {{ purpose?: string }} [options]
 * @returns {Promise<{url: string, key: string}>}
 */
export async function uploadImageFile(selectedFile, options = {}) {
  const formData = new FormData();
  formData.append("file", selectedFile);
  if (options.purpose) {
    formData.append("purpose", options.purpose);
  }
  const response = await fetch(`${API_BASE_URL}/api/upload_image/`, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    let message = "Failed to upload image";
    try {
      const data = await response.json();
      message = data.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const { url, key } = await response.json();
  return { url: url || "", key: key || "" };
}

/**
 * Upload a profile picture or org logo. The server square-crops, resizes, and encodes WebP.
 * @param {File} selectedFile
 * @returns {Promise<{url: string, key: string}>}
 */
export async function uploadAvatarFile(selectedFile) {
  return uploadImageFile(selectedFile, { purpose: "avatar" });
}

/**
 * Append newly picked image files onto the current preview list.
 * @param {File[]} files
 * @param {Array<{id: string, file: File, preview: string}>} current
 * @param {number} [maxImages]
 * @returns {{ items: Array<{id: string, file: File, preview: string}>, error: string }}
 */
export function appendImageFiles(files, current, maxImages = MAX_CONTENT_IMAGES) {
  const limit = Number.isFinite(maxImages) && maxImages > 0 ? maxImages : MAX_CONTENT_IMAGES;
  if (current.length >= limit) {
    return { items: current, error: `You can add up to ${limit} images` };
  }
  const next = [...current];
  let error = "";
  for (const file of files) {
    if (next.length >= limit) {
      error = `You can add up to ${limit} images`;
      break;
    }
    const validationError = validateImageFile(file);
    if (validationError) {
      error = validationError;
      continue;
    }
    next.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      preview: URL.createObjectURL(file),
    });
  }
  return { items: next, error };
}

/**
 * Parse message.metadata JSON into an object.
 * @param {object|null|undefined} message
 * @returns {object}
 */
export function parseMessageMetadata(message) {
  const raw = message?.metadata;
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return {};
  try {
    const meta = JSON.parse(raw);
    return meta && typeof meta === "object" && !Array.isArray(meta) ? meta : {};
  } catch {
    return {};
  }
}

/**
 * Display URLs for images attached to a chat message.
 * @param {object|null|undefined} message
 * @returns {string[]}
 */
export function getMessageImageUrls(message) {
  return getContentImageUrls({ images: parseMessageMetadata(message).images });
}

/**
 * Inbox / reply fallback when an image message has no caption.
 * @param {object|null|undefined} message
 * @returns {string}
 */
export function imageMessagePreviewText(message) {
  if (!message) return "";
  const urls = getMessageImageUrls(message);
  const isImage = message.message_type === "image";
  if (!isImage && !urls.length) return "";
  if (urls.length > 1) return `Sent ${urls.length} photos`;
  return "Sent a photo";
}

/**
 * True when a drag event is carrying files from the OS.
 * @param {DragEvent|{ dataTransfer?: DataTransfer|null }} event
 * @returns {boolean}
 */
export function isFileDragEvent(event) {
  const types = event?.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes("Files");
}

/**
 * Collect image files from a drop or file input.
 * @param {DataTransfer|FileList|File[]|null|undefined} source
 * @returns {File[]}
 */
export function collectImageFiles(source) {
  if (!source) return [];
  const list = source.files || source;
  return Array.from(list).filter((file) => file?.type?.startsWith("image/"));
}
